/**
 * Splash cohorts — shared by the client carousel (`AppWallSplash`) and the
 * server-rendered `/for/<slug>` invite route, so both agree on what a cohort
 * means. Plain module (no server-only / no React) so either side can import it.
 *
 * A cohort floats its lead app ids to the front of the splash deck. It is
 * resolved from a `/for/<slug>` invite, a `?cohort=<key>` link param, or a
 * mapped UTM value, then persisted (localStorage + `rb_cohort` cookie) so it
 * sticks across visits. Add a cohort = one line in COHORTS.
 */

export const COHORTS: Record<string, string[]> = {
  design: ['vector-design', 'video-studio', 'whiteboard'],
  // 'decks' retired — slides live in the vector editor now, so vector-design
  // takes its slot in the cohorts that led with it.
  video: ['video-studio', 'whiteboard', 'vector-design'],
  music: ['guitarro', 'video-studio'],
  // 'tracker' retired with the app (2026-09-13); it had no splash card of its
  // own, so these two cohorts were already floating an id that never matched.
  writing: ['docs', 'vector-design'],
  pm: ['vector-design', 'docs'],
  // E7 (2026-07-28): cohorts lead with a WORKING app; the cohort-scoped
  // "Coming soon" teaser (eda / robongus, injected from COMING_SOON in
  // app-wall-splash.tsx) rides further back in the order. An invited builder's
  // first centered card must open, not tease.
  engineers: ['vector-design', 'docs', 'eda'],
  gamers: ['guitarro', 'video-studio', 'robongus'],
};

// Map marketing UTM values to a cohort key when they don't match a cohort name
// directly (e.g. a campaign slug). Keys are lowercased UTM values.
export const UTM_TO_COHORT: Record<string, string> = {
  designers: 'design',
  figma: 'design',
  'video-creators': 'video',
  musicians: 'music',
  product: 'pm',
  eng: 'engineers',
  engineering: 'engineers',
  developers: 'engineers',
  gaming: 'gamers',
  games: 'gamers',
};

export const COHORT_STORAGE_KEY = 'rb_cohort';

/**
 * Resolve a slug/key to a cohort: a direct cohort key wins, then an alias from
 * `UTM_TO_COHORT` (so `/for/designers` and `/for/design` both → `design`).
 * Returns null for unknown slugs (caller treats it as a plain invite).
 */
export function normalizeCohort(slug: string | null | undefined): string | null {
  const key = (slug || '').trim().toLowerCase();
  if (!key) return null;
  if (COHORTS[key]) return key;
  if (UTM_TO_COHORT[key] && COHORTS[UTM_TO_COHORT[key]]) return UTM_TO_COHORT[key];
  return null;
}
